import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { badgesAPI } from '../utils/api';
import { useLocalBadgeSocket } from '../utils/localBadgeSocket';
import brandLogo from '../assets/logo-centre-jour.png';

const normalizeUid = (value = '') =>
  String(value)
    .trim()
    .toUpperCase()
    .replace(/^0X/, '')
    .replace(/[^A-Z0-9]/g, '');

const BadgeKioskPage = () => {
  const { t } = useTranslation();
  const defaultCenterName = process.env.REACT_APP_BADGE_CENTER_NAME || 'Centre 1';
  const defaultCenterLogo = process.env.REACT_APP_BADGE_CENTER_LOGO || '';
  const defaultSourceSystem = process.env.REACT_APP_BADGE_SOURCE_SYSTEM || 'BADGE_KIOSK';
  const defaultApiKey = process.env.REACT_APP_BADGE_API_KEY || '';
  const initialLang = localStorage.getItem('badgeKioskLang')
    || (String(i18n.language || '').toLowerCase().startsWith('en') ? 'en' : 'fr');

  const [lang, setLang] = useState(initialLang);
  const [clockNow, setClockNow] = useState(new Date());

  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [kioskToken, setKioskToken] = useState('');
  const [apiKey, setApiKey] = useState(localStorage.getItem('badgeKioskApiKey') || defaultApiKey);
  const [centerName, setCenterName] = useState(localStorage.getItem('badgeKioskCenterName') || defaultCenterName);
  const [centerLogo, setCenterLogo] = useState(localStorage.getItem('badgeKioskCenterLogo') || defaultCenterLogo);
  const [deviceId, setDeviceId] = useState(localStorage.getItem('badgeKioskDeviceId') || 'AIO-READER-01');
  const [sourceSystem, setSourceSystem] = useState(localStorage.getItem('badgeKioskSourceSystem') || defaultSourceSystem);

  const [lastUid, setLastUid] = useState('');
  const [lastScanAt, setLastScanAt] = useState(null);
  const lastTriggerRef = useRef({ uid: '', ts: 0 });

  const [scanState, setScanState] = useState('idle');
  const [scanResultType, setScanResultType] = useState('idle');
  const [scanMessage, setScanMessage] = useState('');
  const [scanMeta, setScanMeta] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const resetTimerRef = useRef(null);
  const { connected: socketConnected, lastScan: socketScan, socketError, socketUrl } = useLocalBadgeSocket();

  const tx = useCallback(
    (key, options = {}) => t(`badgeScan.${key}`, options),
    [t]
  );

  useEffect(() => {
    const timer = setInterval(() => setClockNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    localStorage.setItem('badgeKioskLang', lang);
    i18n.changeLanguage(lang);
  }, [lang]);

  const getDefaultWelcomeMessage = useCallback(
    () => tx('welcomeScan', { center: centerName || tx('defaultCenter') }),
    [centerName, tx]
  );

  useEffect(() => {
    localStorage.setItem('badgeKioskApiKey', apiKey);
  }, [apiKey]);
  useEffect(() => {
    localStorage.setItem('badgeKioskCenterName', centerName);
  }, [centerName]);
  useEffect(() => {
    localStorage.setItem('badgeKioskCenterLogo', centerLogo);
  }, [centerLogo]);
  useEffect(() => {
    localStorage.setItem('badgeKioskDeviceId', deviceId);
  }, [deviceId]);
  useEffect(() => {
    localStorage.setItem('badgeKioskSourceSystem', sourceSystem);
  }, [sourceSystem]);
  useEffect(() => {
    localStorage.removeItem('badgeKioskToken');
  }, []);

  useEffect(() => {
    if (scanState === 'idle') {
      setScanMessage(getDefaultWelcomeMessage());
      setScanMeta(tx('autoReading'));
    }
  }, [scanState, getDefaultWelcomeMessage, tx]);

  const etDate = useMemo(
    () =>
      new Intl.DateTimeFormat(lang === 'en' ? 'en-CA' : 'fr-CA', {
        timeZone: 'America/Toronto',
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(clockNow),
    [clockNow, lang]
  );

  const etTime = useMemo(
    () =>
      new Intl.DateTimeFormat(lang === 'en' ? 'en-CA' : 'fr-CA', {
        timeZone: 'America/Toronto',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(clockNow),
    [clockNow, lang]
  );

  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, []);

  const resetScanDisplay = useCallback(() => {
    setScanState('idle');
    setScanResultType('idle');
    setScanMessage(getDefaultWelcomeMessage());
    setScanMeta(tx('autoReading'));
  }, [getDefaultWelcomeMessage, tx]);

  const triggerScan = useCallback(
    async (uidRaw, channel) => {
      const uid = normalizeUid(uidRaw);
      if (!uid) return;
      if (!kioskToken && !apiKey) {
        setScanState('error');
        setScanMessage(tx('noAuth'));
        setScanMeta(tx('noAuthHelp'));
        return;
      }

      const nowTs = Date.now();
      if (lastTriggerRef.current.uid === uid && nowTs - lastTriggerRef.current.ts < 1500) return;
      lastTriggerRef.current = { uid, ts: nowTs };

      setLastUid(uid);
      setLastScanAt(new Date());
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      setScanState('loading');
      setScanResultType('loading');
      setScanMessage(tx('validating'));
      setScanMeta(`UID ${uid} via ${channel}`);

      try {
        const payload = {
          cardUid: uid,
          scannedAt: new Date().toISOString(),
          deviceId: deviceId || undefined,
          sourceSystem: sourceSystem || undefined,
          payload: { channel },
        };

        const res = kioskToken
          ? await badgesAPI.scanKiosk(payload, kioskToken)
          : await badgesAPI.scanExternal(payload, apiKey);
        const eventType = String(res.data?.event_type || '').trim().toUpperCase();
        const isOutEvent = eventType === 'OUT' || !!res.data?.time_clock?.clock_out;
        const userName = res.data?.user?.name || tx('unknownUser');

        if (isOutEvent) {
          setScanState('success');
          setScanResultType('out');
          setScanMessage(`${tx('thanks')} ${userName}`);
          setScanMeta(tx('exitSaved'));
        } else if (eventType === 'IN') {
          setScanState('success');
          setScanResultType('in');
          setScanMessage(`${tx('welcome')} ${userName}`);
          setScanMeta(tx('entrySaved'));
        } else {
          setScanState('success');
          setScanResultType('other');
          setScanMessage(`${userName}`);
          setScanMeta(`${tx('eventLabel')}: ${eventType || 'N/A'}`);
        }
      } catch (err) {
        const apiMessage = String(err.response?.data?.message || '');
        const isUnknownCard = /inconnu|introuvable|desactiv|désactiv/i.test(apiMessage);
        setScanState('error');
        setScanResultType(isUnknownCard ? 'unknown' : 'error');
        setScanMessage(apiMessage || tx('rejectedScan'));
        setScanMeta(tx('checkBadge'));
      }

      resetTimerRef.current = setTimeout(() => {
        resetScanDisplay();
      }, 15000);
    },
    [apiKey, kioskToken, deviceId, sourceSystem, resetScanDisplay, tx]
  );

  useEffect(() => {
    if (!isUnlocked) return;
    if (!socketScan?.uid) return;
    triggerScan(socketScan.uid, `SOCKET:${socketScan.eventName || 'message'}`);
  }, [isUnlocked, socketScan, triggerScan]);

  const unlockKiosk = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await badgesAPI.kioskLogin({
        username: loginUser,
        password: loginPass,
      });

      const token = res.data?.accessToken || '';
      if (!token) {
        setLoginError(tx('invalidSession'));
        return;
      }

      setKioskToken(token);
      if (res.data?.center?.name) setCenterName(res.data.center.name);
      if (res.data?.center?.logoUrl !== undefined) setCenterLogo(res.data.center.logoUrl || '');
      setIsUnlocked(true);
    } catch (err) {
      setLoginError(err.response?.data?.message || tx('invalidCredentials'));
    } finally {
      setLoginLoading(false);
    }
  };

  const statusBg =
    scanResultType === 'out'
      ? 'linear-gradient(120deg, var(--warning-light), #fed7aa)'
      : scanResultType === 'unknown'
        ? 'linear-gradient(120deg, #b91c1c, #dc2626)'
      : scanState === 'success'
      ? 'linear-gradient(120deg, var(--success-light), #dcfce7)'
      : scanState === 'error'
        ? 'linear-gradient(120deg, var(--danger-light), #fee2e2)'
        : 'linear-gradient(120deg, var(--primary-50), #e0f2fe)';

  const langSwitcher = (
    <div style={{ display: 'flex', gap: 8 }}>
      <button type="button" className={`btn btn-sm ${lang === 'fr' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setLang('fr')}>FR</button>
      <button type="button" className={`btn btn-sm ${lang === 'en' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setLang('en')}>EN</button>
    </div>
  );

  if (!isUnlocked) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', padding: 24 }}>
        <div style={{ position: 'fixed', top: 16, right: 16 }}>{langSwitcher}</div>
        <form onSubmit={unlockKiosk} style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 14px 40px rgba(0,0,0,.35)' }}>
          <h2 style={{ margin: 0, marginBottom: 8, fontSize: 28 }}>{tx('badgeKiosk')}</h2>
          <p style={{ marginTop: 0, color: '#64748b', marginBottom: 18 }}>{tx('operatorLogin')}</p>
          {loginError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{loginError}</div>}
          <div className="form-group">
            <label className="form-label">{tx('username')}</label>
            <input className="form-input" value={loginUser} onChange={(e) => setLoginUser(e.target.value)} autoComplete="username" />
          </div>
          <div className="form-group">
            <label className="form-label">{tx('password')}</label>
            <input type="password" className="form-input" value={loginPass} onChange={(e) => setLoginPass(e.target.value)} autoComplete="current-password" />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={loginLoading}>
            {loginLoading ? tx('connecting') : tx('openKiosk')}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)', color: 'var(--gray-800)', display: 'grid', gridTemplateRows: 'auto 1fr auto' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 26px', borderBottom: '1px solid var(--gray-200)', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={centerLogo || brandLogo} alt="Centre" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 12, background: '#fff', border: '1px solid var(--gray-200)' }} />
          <div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{centerName || tx('defaultCenter')}</div>
            <div className="text-muted">{etDate}</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 58, lineHeight: 1, fontWeight: 700, letterSpacing: -2, color: 'var(--gray-900)' }}>{etTime}</div>
          <div className="text-muted">{tx('timezone')}</div>
        </div>
      </header>

      <main style={{ padding: 20, display: 'grid', gridTemplateColumns: showConfig ? '360px 1fr' : '1fr', gap: 16 }}>
        {showConfig && (
          <section className="card card-form">
            <h3 style={{ marginTop: 0 }}>{tx('configMode')}</h3>

            <div className="form-group">
              <label className="form-label">{tx('apiKey')}</label>
              <input className="form-input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="clé-api-badge" />
            </div>
            <div className="form-group">
              <label className="form-label">{tx('centerName')}</label>
              <input className="form-input" value={centerName} onChange={(e) => setCenterName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{tx('logoUrl')}</label>
              <input className="form-input" value={centerLogo} onChange={(e) => setCenterLogo(e.target.value)} placeholder="https://..." />
            </div>
            <div className="form-group">
              <label className="form-label">{tx('deviceId')}</label>
              <input className="form-input" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{tx('sourceSystem')}</label>
              <input className="form-input" value={sourceSystem} onChange={(e) => setSourceSystem(e.target.value)} />
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gray-500)' }}>
              <div>{tx('socketLocal')}: {socketConnected ? tx('connected') : tx('disconnected')}</div>
              <div>{tx('socketUrl')}: {socketUrl}</div>
              {socketError && <div style={{ color: 'var(--danger)' }}>{socketError}</div>}
            </div>
          </section>
        )}

        <section style={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 14 }}>
          <div style={{ padding: 18, borderRadius: 16, border: '1px solid var(--gray-200)', background: statusBg, color: 'var(--gray-900)', minHeight: 220 }}>
            <div style={{ fontSize: 20, opacity: 0.8, textAlign: 'center' }}>{tx('statusScan')}</div>
            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.15, marginTop: 6, textAlign: 'center' }}>{scanMessage}</div>
            <div style={{ marginTop: 10, fontSize: 18, textAlign: 'center' }}>{scanMeta || tx('waitingBadge')}</div>
            {lastUid && (
              <div style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 18, textAlign: 'center' }}>
                UID: <strong>{lastUid}</strong>{' '}
                {lastScanAt ? `(${lastScanAt.toLocaleTimeString(lang === 'en' ? 'en-CA' : 'fr-CA')})` : ''}
              </div>
            )}
          </div>

          <div style={{ border: '1px dashed var(--gray-300)', borderRadius: 16, display: 'grid', placeItems: 'center', padding: 24, background: '#fff' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 110, lineHeight: 1 }}>🪪</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{tx('presentBadge')}</div>
              <div style={{ marginTop: 8, color: 'var(--gray-500)' }}>
                {tx('autoInOut')}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer style={{ padding: '10px 24px', borderTop: '1px solid var(--gray-200)', background: '#fff', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
        <span>{tx('localKiosk')}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {langSwitcher}
          <button className="btn btn-sm btn-outline" onClick={() => setShowConfig((v) => !v)}>
            {showConfig ? tx('hideConfig') : tx('showConfig')}
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => {
              setIsUnlocked(false);
              setKioskToken('');
            }}
          >
            {tx('lock')}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default BadgeKioskPage;
